/**
 * Crops — Platform Admin страница справочника культур (AK-231 Pilot).
 *
 * Тонкий wrapper над `ReferenceCatalogList` + `CropEditorDrawer`. Это эталон
 * паттерна reference-CRUD для оставшихся 7 справочников эпика AK-226.
 * README см. `frontend-vite/src/pages/platform-admin/reference/README.md`.
 *
 * Доступ: Platform Admin role-guard в `PlatformAdminLayout`.
 *
 * Источник дизайна:
 *   - wireframe: `docs/superpowers/prototypes/ak231-cultures/index.html`
 *   - SA: `docs/superpowers/specs/2026-06-21-ak231-cultures-reference-sa.md`
 */
import { useCallback, useMemo, useState } from 'react';
import {
  Archive,
  Copy,
  GitMerge,
  MoreHorizontal,
  Pencil,
  Repeat,
  Snowflake,
  Star,
  X,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PlatformAdminLayout } from '@/layouts/PlatformAdminLayout';
import {
  ReferenceCatalogList,
  type ReferenceColumn,
} from '@/components/reference/ReferenceCatalogList';
import {
  ReferenceMergeDialog,
  type MergeCandidate,
} from '@/components/reference/ReferenceMergeDialog';
import { CropEditorDrawer, type FormState as CropFormState } from './CropEditorDrawer';
import {
  useArchiveCrop,
  useCategories,
  useCreateCrop,
  useCropGroups,
  useCropsInfinite,
  useGoals,
  useMergeCropsBatch,
  useMergeCrops,
  useUpdateCrop,
} from './hooks/useReferenceQueries';
import type {
  Crop,
  CropCreatePayload,
  CropsListQuery,
} from './__mocks__/reference';
import {
  EMPTY_SELECTION,
  canMerge as canMergeSel,
  isKeeper,
  isSelected,
  selectedCount,
  setKeeper,
  sourceIds as sourceIdsSel,
  toggle,
  type MergeSelection,
} from './cropMergeSelection';
import { cn } from '@/lib/utils';
import { toast } from '@/lib/toast';

function formatTkw(min: number | null, max: number | null): string {
  if (min === null && max === null) return '—';
  if (min !== null && max !== null) return `${min}–${max} г`;
  return `${min ?? max} г`;
}

function formatUpdatedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('ru-RU', {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return '—';
  }
}

export function PlatformAdminCropsPage() {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [groupFilter, setGroupFilter] = useState<string>('all');
  const [onlyWinter, setOnlyWinter] = useState(false);
  const [onlyPerennial, setOnlyPerennial] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingCrop, setEditingCrop] = useState<Crop | null>(null);
  /**
   * AK-239: при «Создать на основе» — pre-fill state для create-режима.
   * Хранит partial FormState (всё кроме code+name), который drawer применит
   * через initialOverride prop. null = обычный create с EMPTY_FORM.
   */
  const [cloneSource, setCloneSource] = useState<Crop | null>(null);

  const query: CropsListQuery = useMemo(
    () => ({
      search: search.trim() || undefined,
      category_id: categoryFilter === 'all' ? undefined : categoryFilter,
      group_id: groupFilter === 'all' ? undefined : groupFilter,
      is_winter: onlyWinter ? true : undefined,
      is_perennial: onlyPerennial ? true : undefined,
      include_archived: includeArchived,
    }),
    [search, categoryFilter, groupFilter, onlyWinter, onlyPerennial, includeArchived],
  );

  const cropsQ = useCropsInfinite(query);
  const categoriesQ = useCategories();
  const groupsQ = useCropGroups();
  const goalsQ = useGoals();

  const createMut = useCreateCrop();
  const updateMut = useUpdateCrop();
  const archiveMut = useArchiveCrop();
  const mergeMut = useMergeCrops();
  const mergeBatchMut = useMergeCropsBatch();

  const items = cropsQ.data?.items ?? [];
  const categories = categoriesQ.data?.items ?? [];
  const groups = groupsQ.data?.items ?? [];
  const goals = goalsQ.data?.items ?? [];
  const total = cropsQ.data?.total ?? 0;

  const handleCreate = () => {
    setEditingCrop(null);
    setCloneSource(null);
    setDrawerOpen(true);
  };

  const handleEdit = (c: Crop) => {
    setEditingCrop(c);
    setCloneSource(null);
    setDrawerOpen(true);
  };

  /**
   * AK-239: «Создать на основе» — превращаем edit-сессию в create-сессию
   * с pre-filled полями. Сброс editingCrop=null заставит drawer перейти
   * в mode='create'; cloneSource даст ему initialOverride.
   */
  const handleClone = (source: Crop) => {
    setEditingCrop(null);
    setCloneSource(source);
    // drawer уже открыт — оставляем
  };

  const handleDrawerOpenChange = (next: boolean) => {
    setDrawerOpen(next);
    if (!next) {
      // При закрытии — сбрасываем clone-source, чтобы следующий create
      // не унаследовал старый pre-fill.
      setCloneSource(null);
    }
  };

  const handleSubmit = async (payload: CropCreatePayload) => {
    if (editingCrop) {
      await updateMut.mutateAsync({ id: editingCrop.id, payload });
      toast.success(
        `Культура «${payload.name_translations.ru ?? payload.code}» сохранена`,
      );
    } else {
      await createMut.mutateAsync(payload);
      // AK-239: если это clone — даём пользователю понять что создана новая.
      if (cloneSource) {
        toast.success(
          `Создана новая культура на основе «${cloneSource.name_translations.ru ?? cloneSource.code}»`,
        );
        setCloneSource(null);
      } else {
        toast.success(
          `Культура «${payload.name_translations.ru ?? payload.code}» создана`,
        );
      }
    }
  };

  /**
   * AK-239: строим partial FormState из source-крупа — копируем всё кроме
   * code/name (их пользователь обязан задать заново). Phenophases sub-table
   * в drawer'е не активна в create-режиме (см. CropEditorDrawer.tsx
   * «Сначала сохраните культуру — тогда станут доступны фенофазы»), так что
   * фазы клонировать на этом этапе нельзя — следующий шаг (post-MVP), когда
   * пользователь захочет одновременный clone с фазами, мы вернёмся отдельно.
   */
  const cloneInitialOverride = useMemo<Partial<CropFormState> | null>(() => {
    if (!cloneSource) return null;
    return {
      // code: пусто — пользователь введёт новый
      // name_translations: пусто — пользователь введёт новое
      name_translations: { ru: '' },
      latin_name: cloneSource.latin_name ?? '',
      color: cloneSource.color,
      category_id: cloneSource.category_id ?? '',
      growing_goal_ids: [...cloneSource.growing_goal_ids],
      is_winter: cloneSource.is_winter,
      is_perennial: cloneSource.is_perennial,
      // AK-260 — Crop carries canonical DB names; FormState uses short UI names.
      tkw_min:
        cloneSource.thousand_seeds_weight_min_g == null
          ? ''
          : String(cloneSource.thousand_seeds_weight_min_g),
      tkw_max:
        cloneSource.thousand_seeds_weight_max_g == null
          ? ''
          : String(cloneSource.thousand_seeds_weight_max_g),
      sowing_season: cloneSource.sowing_season ?? ('' as const),
      default_seeding_rate:
        cloneSource.default_seeding_rate_min == null
          ? ''
          : String(cloneSource.default_seeding_rate_min),
    };
  }, [cloneSource]);

  const cropLabel = (c: Crop) => c.name_translations.ru ?? c.code;

  // ---------- AK-271: multiselect эталон-первым + batch merge ----------
  const [selection, setSelection] = useState<MergeSelection>(EMPTY_SELECTION);
  const [batchOpen, setBatchOpen] = useState(false);

  const itemById = useMemo(() => {
    const m = new Map<string, Crop>();
    for (const c of items) m.set(c.id, c);
    return m;
  }, [items]);

  const handleToggleSelect = useCallback((id: string) => {
    setSelection((s) => toggle(s, id));
  }, []);

  const handleSetKeeper = useCallback((id: string) => {
    setSelection((s) => setKeeper(s, id));
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelection(EMPTY_SELECTION);
  }, []);

  const keeperCrop = selection.keeperId
    ? (itemById.get(selection.keeperId) ?? null)
    : null;
  const selectedN = selectedCount(selection);
  const batchSourceIds = sourceIdsSel(selection);
  const canDoBatch = canMergeSel(selection);

  /** Кандидаты для batch-диалога: эталон (target) + источники из текущего выбора. */
  const batchTargetCandidate = useMemo<MergeCandidate | null>(() => {
    if (!keeperCrop) return null;
    return {
      id: keeperCrop.id,
      label: cropLabel(keeperCrop),
      sublabel: keeperCrop.latin_name ?? keeperCrop.code,
    };
  }, [keeperCrop]);

  const batchSourceCandidates = useMemo<MergeCandidate[]>(
    () =>
      batchSourceIds
        .map((id) => itemById.get(id))
        .filter((c): c is Crop => Boolean(c))
        .map((c) => ({
          id: c.id,
          label: cropLabel(c),
          sublabel: c.latin_name ?? c.code,
        })),
    [batchSourceIds, itemById],
  );

  const handleBatchPreview = useCallback(
    async (targetId: string, srcIds: string[]) => {
      const res = await mergeBatchMut.mutateAsync({
        targetId,
        sourceIds: srcIds,
        dryRun: true,
      });
      return {
        totalRepointed: res.total_repointed,
        totalDeleted: res.total_deleted,
        sourceCount: res.source_count,
      };
    },
    [mergeBatchMut.mutateAsync],
  );

  const handleBatchConfirm = useCallback(
    async (targetId: string, srcIds: string[]) => {
      const targetLabel = keeperCrop ? cropLabel(keeperCrop) : 'эталон';
      try {
        await mergeBatchMut.mutateAsync({
          targetId,
          sourceIds: srcIds,
          dryRun: false,
        });
        toast.success(
          `Объединено ${srcIds.length} в эталон «${targetLabel}»`,
        );
        // Список инвалидируется хуком — источники уйдут в архив. Сбрасываем
        // выбор, чтобы sticky-панель исчезла без ручной перезагрузки.
        setSelection(EMPTY_SELECTION);
      } catch (err) {
        toast.error((err as Error).message);
        throw err; // не закрываем диалог при ошибке
      }
    },
    [mergeBatchMut.mutateAsync, keeperCrop],
  );

  // ---------- AK-248: per-row picker merge ("Поглотить") ----------
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState<Crop | null>(null);

  const pickerTargetCandidate = useMemo<MergeCandidate | null>(() => {
    if (!pickerTarget) return null;
    return {
      id: pickerTarget.id,
      label: cropLabel(pickerTarget),
      sublabel: pickerTarget.latin_name ?? pickerTarget.code,
    };
  }, [pickerTarget]);

  const pickerCandidates = useMemo<MergeCandidate[]>(
    () =>
      items
        .filter((c) => c.id !== pickerTarget?.id)
        .map((c) => ({
          id: c.id,
          label: cropLabel(c),
          sublabel: c.latin_name ?? c.code,
          archived: Boolean(c.archived_at),
        })),
    [items, pickerTarget?.id],
  );

  const handlePickerPreview = useCallback(
    async (sourceId: string, targetId: string) => {
      const res = await mergeMut.mutateAsync({ sourceId, targetId, dryRun: true });
      return {
        totalRepointed: res.total_repointed,
        totalDeleted: res.total_deleted,
      };
    },
    [mergeMut.mutateAsync],
  );

  const handlePickerConfirm = useCallback(
    async (sourceId: string, targetId: string) => {
      const targetLabel = pickerTarget ? cropLabel(pickerTarget) : 'эталон';
      try {
        await mergeMut.mutateAsync({ sourceId, targetId, dryRun: false });
        toast.success(`Поглощено в «${targetLabel}»`);
      } catch (err) {
        toast.error((err as Error).message);
        throw err;
      }
    },
    [mergeMut.mutateAsync, pickerTarget],
  );

  const handleArchive = async (c: Crop) => {
    if (!window.confirm(`Архивировать «${c.name_translations.ru ?? c.code}»?`)) {
      return;
    }
    try {
      await archiveMut.mutateAsync(c.id);
      toast.success(
        `Архивировано. Восстановить можно через фильтр «архивные».`,
      );
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const columns: ReferenceColumn<Crop>[] = [
    {
      // AK-271: чекбокс мультивыбора + звезда-эталон. Архивные нельзя выбирать.
      key: 'select',
      header: '',
      width: '76px',
      render: (c) => {
        const archived = Boolean(c.archived_at);
        const selected = isSelected(selection, c.id);
        const keeper = isKeeper(selection, c.id);
        return (
          <div className="flex items-center gap-1.5">
            <Checkbox
              checked={selected}
              disabled={archived}
              onCheckedChange={() => handleToggleSelect(c.id)}
              aria-label={`Выбрать ${c.name_translations.ru ?? c.code} для объединения`}
            />
            {selected ? (
              <button
                type="button"
                onClick={() => handleSetKeeper(c.id)}
                aria-label={
                  keeper
                    ? `${c.name_translations.ru ?? c.code} — эталон`
                    : `Сделать ${c.name_translations.ru ?? c.code} эталоном`
                }
                aria-pressed={keeper}
                className={cn(
                  'inline-flex h-6 w-6 items-center justify-center rounded-md transition-colors',
                  keeper
                    ? 'text-accent-brand'
                    : 'text-muted-foreground hover:text-accent-brand',
                )}
                title={keeper ? 'Эталон (выживает)' : 'Сделать эталоном'}
              >
                <Star
                  className={cn('h-4 w-4', keeper && 'fill-accent-brand')}
                />
              </button>
            ) : (
              <span className="inline-block h-6 w-6" aria-hidden />
            )}
          </div>
        );
      },
    },
    {
      key: 'color',
      header: 'Цвет',
      width: '56px',
      render: (c) => (
        <span
          className="inline-block h-4 w-4 rounded border border-border"
          style={{ backgroundColor: c.color }}
          aria-label={`Цвет ${c.color}`}
        />
      ),
    },
    {
      key: 'name',
      header: 'Название',
      render: (c) => (
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => handleEdit(c)}
              className="text-left font-medium text-foreground hover:text-primary"
            >
              {c.name_translations.ru ?? c.code}
            </button>
            {isKeeper(selection, c.id) && (
              <Badge variant="accent-brand" className="gap-1">
                <Star className="h-3 w-3 fill-current" />
                Эталон
              </Badge>
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            {c.name_translations.en ?? c.latin_name ?? c.code}
          </div>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Товарная группа',
      width: '170px',
      render: (c) =>
        c.category_name ? (
          <Badge variant="secondary">{c.category_name}</Badge>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      key: 'group',
      header: 'Биологическая культура',
      width: '190px',
      render: (c) =>
        c.group_name ? (
          <Badge variant="outline">{c.group_name}</Badge>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      key: 'badges',
      header: 'Признаки',
      width: '220px',
      render: (c) => {
        const has = c.is_winter || c.is_perennial || c.archived_at;
        if (!has) return <span className="text-xs text-muted-foreground">—</span>;
        return (
          <div className="flex flex-wrap items-center gap-1">
            {c.is_winter && (
              <Badge variant="accent-brand" className="gap-1">
                <Snowflake className="h-3 w-3" />
                озимая
              </Badge>
            )}
            {c.is_perennial && (
              <Badge variant="primary" className="gap-1">
                <Repeat className="h-3 w-3" />
                многолетняя
              </Badge>
            )}
            {c.archived_at && (
              <Badge variant="outline" className="gap-1">
                <Archive className="h-3 w-3" />
                архив
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      key: 'tkw',
      header: 'TKW',
      width: '110px',
      render: (c) => (
        <span className="text-xs tabular-nums text-muted-foreground">
          {formatTkw(c.thousand_seeds_weight_min_g, c.thousand_seeds_weight_max_g)}
        </span>
      ),
    },
    {
      key: 'updated',
      header: 'Изменена',
      width: '110px',
      render: (c) => (
        <span className="text-xs text-muted-foreground">
          {formatUpdatedAt(c.updated_at)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '50px',
      align: 'right',
      render: (c) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
              aria-label={`Действия для ${c.name_translations.ru ?? c.code}`}
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onSelect={() => handleEdit(c)}>
              <Pencil className="h-4 w-4" />
              Редактировать
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => {
                // AK-239: открываем drawer уже как create-with-prefill.
                setEditingCrop(null);
                setCloneSource(c);
                setDrawerOpen(true);
              }}
              disabled={Boolean(c.archived_at)}
            >
              <Copy className="h-4 w-4" />
              Создать на основе
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => {
                setPickerTarget(c);
                setPickerOpen(true);
              }}
              disabled={Boolean(c.archived_at)}
            >
              <GitMerge className="h-4 w-4" />
              Поглотить
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => {
                void handleArchive(c);
              }}
              disabled={Boolean(c.archived_at)}
            >
              <Archive className="h-4 w-4" />
              {c.archived_at ? 'Уже архивирована' : 'Архивировать'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <PlatformAdminLayout>
      <ReferenceCatalogList<Crop>
        title="Товарные культуры"
        subtitle={`Системный справочник · read-only baseline для всех хозяйств · ${total} ${total === 1 ? 'запись' : 'записей'}`}
        createLabel="Создать культуру"
        onCreate={handleCreate}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Поиск по названию или коду…"
        items={items}
        isLoading={cropsQ.isLoading}
        isError={cropsQ.isError}
        error={cropsQ.error ?? undefined}
        onRetry={() => cropsQ.refetch()}
        getRowKey={(c) => c.id}
        total={total}
        unitLabel="культур"
        hasMore={!!cropsQ.hasNextPage}
        loadingMore={cropsQ.isFetchingNextPage}
        onLoadMore={() => cropsQ.fetchNextPage()}
        columns={columns}
        emptyTitle="Пока нет ни одной культуры"
        emptyHint="Создайте первую культуру — название, цвет на карте и агрохарактеристики станут доступны всем хозяйствам платформы."
        toolbarSlot={
          <>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[170px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все категории</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name_translations.ru ?? c.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={groupFilter} onValueChange={setGroupFilter}>
              <SelectTrigger className="w-[170px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все биологические культуры</SelectItem>
                {groups.map((g) => (
                  <SelectItem key={g.id} value={g.id}>
                    {g.name_translations.ru ?? g.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5">
              <Checkbox
                id="filter-winter"
                checked={onlyWinter}
                onCheckedChange={(v) => setOnlyWinter(v === true)}
              />
              <Label htmlFor="filter-winter" className="cursor-pointer text-xs">
                озимая
              </Label>
            </div>
            <div className="flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5">
              <Checkbox
                id="filter-perennial"
                checked={onlyPerennial}
                onCheckedChange={(v) => setOnlyPerennial(v === true)}
              />
              <Label htmlFor="filter-perennial" className="cursor-pointer text-xs">
                многолетняя
              </Label>
            </div>
            <div className="flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5">
              <Checkbox
                id="filter-archived"
                checked={includeArchived}
                onCheckedChange={(v) => setIncludeArchived(v === true)}
              />
              <Label htmlFor="filter-archived" className="cursor-pointer text-xs">
                архивные
              </Label>
            </div>
          </>
        }
      />

      <CropEditorDrawer
        open={drawerOpen}
        onOpenChange={handleDrawerOpenChange}
        mode={editingCrop ? 'edit' : 'create'}
        crop={editingCrop}
        categories={categories}
        groups={groups}
        goals={goals}
        onSubmit={handleSubmit}
        initialOverride={cloneInitialOverride}
        onCloneClick={handleClone}
      />

      {/* AK-248: per-row picker merge ("Поглотить") */}
      <ReferenceMergeDialog
        mode="picker"
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        target={pickerTargetCandidate}
        candidates={pickerCandidates}
        entityAccusative="культуру"
        onPreview={handlePickerPreview}
        onConfirm={handlePickerConfirm}
      />

      {/* AK-271 batch confirm dialog */}
      {batchTargetCandidate && (
        <ReferenceMergeDialog
          mode="batch"
          open={batchOpen}
          onOpenChange={setBatchOpen}
          target={batchTargetCandidate}
          sources={batchSourceCandidates}
          entityAccusative="культуры"
          onPreviewBatch={handleBatchPreview}
          onConfirmBatch={handleBatchConfirm}
        />
      )}

      {/* AK-271 sticky action bar — fixed к низу viewport, адаптивно (CLAUDE.md). */}
      {selectedN > 0 && (
        <div className="pointer-events-none fixed inset-x-0 bottom-0 z-30 flex justify-center px-4 pb-4">
          <div className="pointer-events-auto flex w-full max-w-3xl flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-border bg-card px-4 py-3 shadow-lg">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              <span className="font-medium text-foreground">
                Выбрано {selectedN}
              </span>
              {keeperCrop ? (
                <span className="flex min-w-0 items-center gap-1 text-muted-foreground">
                  <Star className="h-3.5 w-3.5 shrink-0 fill-accent-brand text-accent-brand" />
                  Эталон:
                  <span className="truncate font-medium text-foreground">
                    {cropLabel(keeperCrop)}
                  </span>
                </span>
              ) : (
                <span className="text-muted-foreground">
                  Отметьте звездой эталон
                </span>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleClearSelection}
              >
                <X className="h-4 w-4" />
                Сбросить
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={!canDoBatch}
                onClick={() => setBatchOpen(true)}
              >
                <GitMerge className="h-4 w-4" />
                Объединить {batchSourceIds.length} в эталон
              </Button>
            </div>
          </div>
        </div>
      )}

    </PlatformAdminLayout>
  );
}

export default PlatformAdminCropsPage;
